



















































































































import { defineComponent, PropType } from '@vue/composition-api';
import {
  EnerIQSuggestionList,
  SeverityColor,
  excludeHeatingSystemComponentNameList,
  HeatingSystemComponentKeyUpperCase,
} from '@/util/heating-systems';
import RelativeTime from '@/features/ui/datetime/RelativeTime.global.vue';
import dayjs from 'dayjs';
import { HeatingSystem } from '@/util/modbus';
import StatusTabSkeletons from './StatusTabSkeletons.vue';
import StatusTabHistory from './StatusTabHistory.vue';

export interface EiqMessage {
  color: SeverityColor;
  message: string;
}

interface HeatingSystemComponentStatus {
  spotId: string;
  name: string;
  score: number | null;
  lastPayload: string | null;
  messages: EiqMessage[];
}

type Measurement = { spotId: string };
type MeasurementGroup = { groupName: string; heatingSystemMeasurements: Measurement[] };

export const severityOrder = {
  green: 0,
  yellow: 1,
  red: 2,
  gray: 3,
};

export default defineComponent({
  name: 'StatusTab',
  components: {
    StatusTabSkeletons,
    RelativeTime,
    StatusTabHistory,
  },
  props: {
    heatingSystem: {
      type: Object as PropType<HeatingSystem>,
      required: true,
    },
  },
  data() {
    return {
      openSpotIds: [] as string[],
      selectedSpotId: '',
      filteredHeatingSystem: {} as HeatingSystem,
      uniqueSpotIdsAndGroupNames: [] as { spotId: string; groupName: string }[],
      eneriqResults: [],
      eneriqLoading: false,
      showHistory: false,
    };
  },
  computed: {
    componentStatus(): Array<HeatingSystemComponentStatus> {
      if (this.eneriqLoading) {
        return [];
      }
      return this.processEnerIQResults();
    },
  },
  watch: {
    heatingSystem: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.setFilteredHeatingSystem();
          this.setUniqueSpotIdsAndGroupNames();
          this.fetchEnerIQResultsForModal();
        }
      },
    },
    componentStatus: {
      handler() {
        this.openAllPanels();
      },
    },
  },
  mounted() {
    if (this.componentStatus.length > 0 && this.openSpotIds.length === 0) {
      this.openSpotIds = [this.componentStatus[0].spotId];
    }
  },
  methods: {
    sanitizeHtml(html: string): string {
      // Remove script/style tags and their content
      html = html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
      html = html.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '');
      // Remove event handlers like onclick, onerror, etc.
      html = html.replace(/ on\w+="[^"]*"/gi, '');
      html = html.replace(/ on\w+='[^']*'/gi, '');
      // Remove javascript: in href/src
      html = html.replace(/(href|src)\s*=\s*(['"])javascript:[^'"]*\2/gi, '$1="#"');
      return html;
    },
    setFilteredHeatingSystem() {
      const filteredGroups = this.heatingSystem.heatingSystemMeasurementGroups.filter(
        (group) =>
          !excludeHeatingSystemComponentNameList.includes(
            group.systemComponentTypeName as HeatingSystemComponentKeyUpperCase,
          ),
      );
      this.filteredHeatingSystem = { ...this.heatingSystem, heatingSystemMeasurementGroups: filteredGroups };
    },
    setUniqueSpotIdsAndGroupNames() {
      const filteredSpotIdsAndGroupNames = this.filteredHeatingSystem.heatingSystemMeasurementGroups.flatMap(
        (group: MeasurementGroup) =>
          group.heatingSystemMeasurements.map((m: Measurement) => ({
            spotId: m.spotId,
            groupName: group.groupName,
          })),
      );

      this.uniqueSpotIdsAndGroupNames = [
        ...new Map(filteredSpotIdsAndGroupNames.map((item) => [item.spotId, item])).values(),
      ];
    },
    async fetchEnerIQResultsForModal() {
      this.eneriqLoading = true;
      try {
        const spotIds = this.uniqueSpotIdsAndGroupNames.map((item) => item.spotId);
        // Fetch EnerIQ results for the modal
        if (spotIds.length > 0) {
          const results = await this.$store.dispatch('getLatestEnerIQSuggestion', spotIds.join(','));
          const data = results?.ok ? await results.json() : [];
          this.eneriqResults = data;
        } else {
          this.eneriqResults = [];
        }
      } catch (e) {
        this.eneriqResults = [];
      } finally {
        this.eneriqLoading = false;
      }
    },
    processEnerIQResults(): Array<{
      spotId: string;
      name: string;
      score: number | null;
      lastPayload: string | null;
      messages: EiqMessage[];
    }> {
      const apiMap = new Map(
        ((this.eneriqResults as EnerIQSuggestionList[]) ?? []).map((suggestionList: EnerIQSuggestionList) => [
          suggestionList.spotId,
          suggestionList,
        ]),
      );

      const componentStatus: Array<HeatingSystemComponentStatus> = [];

      this.uniqueSpotIdsAndGroupNames.forEach(({ spotId, groupName }) => {
        const apiData = apiMap.get(spotId);
        if (apiData) {
          const { operations } = apiData.suggestion.score.metadata.reason;
          const messages: EiqMessage[] = Object.values(operations)
            .filter((operation) => operation.severity !== -1)
            .map((operation) => {
              const message =
                typeof operation.description === 'object' ? operation.description.de : operation.description;
              return {
                color: this.getEnerIQSeverityColor(operation.severity),
                message,
              };
            });
          componentStatus.push({
            spotId,
            name: groupName,
            score: apiData.suggestion.score.score,
            lastPayload: apiData.suggestion.score.time ?? 'N/A',
            messages,
          });
        } else {
          componentStatus.push({
            spotId,
            name: groupName,
            score: null,
            lastPayload: null,
            messages: [
              {
                color: SeverityColor.GRAY,
                message: 'Keine Analyse oder Empfehlungen verfügbar.',
              },
            ],
          });
        }
      });
      return componentStatus;
    },
    getEnerIQSeverityColor(severity: number): SeverityColor {
      if (severity === -1) return SeverityColor.GRAY;
      if (severity === 0) return SeverityColor.RED;
      if (severity > 0 && severity <= 70) return SeverityColor.YELLOW;
      if (severity > 70 && severity <= 99) return SeverityColor.GREEN;
      if (severity === 100) return SeverityColor.GREEN;
      return SeverityColor.GRAY;
    },
    formattedMessage(message: string): string {
      let formatted = message.replace(/Handlungsempfehlungen:/g, '<strong>Handlungsempfehlungen:</strong>');
      formatted = formatted.replace(/\n+/g, '<br>');
      formatted = formatted.replace(/<br>- (.*?)(?=<br>|$)/g, '<li>$1</li>');
      formatted = formatted.replace(
        /((?:<li>.*?<\/li>)+)/g,
        '<ul style="margin: 0.5em 0 0.5em 1.2em; padding: 0;">$1</ul>',
      );
      formatted = formatted.replace(/<br><ul/g, '<ul');
      formatted = formatted.replace(/\*+/g, '');

      return this.sanitizeHtml(formatted);
    },
    toggleSpotId(spotId: string) {
      const index = this.openSpotIds.indexOf(spotId);
      if (index === -1) {
        this.openSpotIds.push(spotId);
      } else {
        this.openSpotIds.splice(index, 1);
      }
    },
    isSpotIdOpen(spotId: string) {
      return this.openSpotIds.includes(spotId);
    },
    openSpotId(spotId: string) {
      if (!this.openSpotIds.includes(spotId)) {
        this.openSpotIds.push(spotId);
      }
    },
    closeSpotId(spotId: string) {
      const index = this.openSpotIds.indexOf(spotId);
      if (index !== -1) {
        this.openSpotIds.splice(index, 1);
      }
    },
    openAndScrollToSpot(spotId: string) {
      this.openSpotId(spotId);
      this.$nextTick(() => {
        const rightPanel = this.$el.querySelector('.right-panel');
        const el = this.$refs[spotId];
        if (rightPanel && el && (Array.isArray(el) ? el[0] : el)) {
          const target = Array.isArray(el) ? el[0] : el;
          const targetEl = target as HTMLElement;
          const rightPanelEl = rightPanel as HTMLElement;
          rightPanelEl.scrollTop = targetEl.offsetTop - rightPanelEl.offsetTop;
        }
      });
    },
    formatDate(date: string) {
      return dayjs(date).format('DD.MM.YY, HH:mm:ss');
    },
    sortMessagesBySeverity(messages: EiqMessage[]) {
      return [...messages].sort(
        (a, b) =>
          severityOrder[(a.color || 'gray').toLowerCase() as keyof typeof severityOrder] -
          severityOrder[(b.color || 'gray').toLowerCase() as keyof typeof severityOrder],
      );
    },
    getWorstSeverityColor(messages: EiqMessage[]): 'red' | 'yellow' | 'green' | 'gray' {
      if (messages.some((msg) => msg.color === SeverityColor.RED)) return 'red';
      if (messages.some((msg) => msg.color === SeverityColor.YELLOW)) return 'yellow';
      if (messages.some((msg) => msg.color === SeverityColor.GREEN)) return 'green';

      return 'gray';
    },
    getSignalLightClassByMessages(messages: EiqMessage[], color: 'red' | 'yellow' | 'green' | 'gray'): 'on' | '' {
      const worst = this.getWorstSeverityColor(messages);
      return worst === color ? 'on' : '';
    },
    handleSelect(spotId: string) {
      this.selectedSpotId = spotId;
      this.openSpotId(spotId);
      this.$nextTick(() => {
        this.scrollToPanel(spotId);
      });
    },
    onPanelUpdate(selected: boolean, spotId: string) {
      if (selected) {
        this.openSpotIds = this.openSpotIds.filter((id) => id !== spotId);
      } else {
        this.openSpotIds = [...this.openSpotIds, spotId];
      }
      this.selectedSpotId = spotId;
      this.$nextTick(() => {
        this.scrollToPanel(spotId);
        this.scrollLeftToSpot(spotId);
      });
    },
    onPanelTitleClick(spotId: string) {
      this.toggleSpotId(spotId);
      this.selectedSpotId = spotId;
      this.scrollLeftToSpot(spotId);
    },
    onRightBlockClick(spotId: string) {
      this.selectedSpotId = spotId;
      this.scrollLeftToSpot(spotId);
    },
    scrollToPanel(spotId: string) {
      this.$nextTick(() => {
        const rightPanelScroll = this.$el.querySelector('.right-panel-scroll');
        let el = this.$refs[spotId];
        if (Array.isArray(el)) {
          el = el[0];
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (el && (el as import('vue/types/vue').Vue).$el) {
          el = (el as import('vue/types/vue').Vue).$el;
        }
        if (rightPanelScroll && el) {
          (el as HTMLElement).scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
          const elRect = (el as HTMLElement).getBoundingClientRect();
          const scrollRect = (rightPanelScroll as HTMLElement).getBoundingClientRect();
          if (elRect.bottom > scrollRect.bottom) {
            (rightPanelScroll as HTMLElement).scrollTop += elRect.bottom - scrollRect.bottom;
          }
        }
      });
    },
    scrollLeftToSpot(spotId: string) {
      this.$nextTick(() => {
        const leftPanel = this.$el.querySelector('.left-panel ul');
        let el = this.$refs[`left-${spotId}`];
        if (Array.isArray(el)) el = el[0];
        if (leftPanel && el) {
          (el as HTMLElement).scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }
      });
    },
    openAllPanels() {
      this.openSpotIds = this.componentStatus.map((comp) => comp.spotId);
    },
    openHistoryTab() {
      this.showHistory = true;
    },
    closeHistoryTab() {
      this.showHistory = false;
    },
  },
});
